import React, { useEffect, useRef, useState } from "react";
import {getConfig} from "../AppConfig";
import LoadingBackdrop from "./common/LoadingBackdrop";
import {useNetworkOnline} from "../slices/authSlice";
import {db} from "../pages/WorkReportingPage/db";
import PdfFilenameDialog from "./PdfFilenameDialog";
import ImportFromDocumentsDialog from "./ImportFromDocumentsDialog";
import {fetchDocumentAsBlob} from "../util/documentFetch";
import Snackbar from "@mui/material/Snackbar";
import {Alert, ClickAwayListener} from "@mui/material";

export default function PdfViewerComponent(props) {
    const containerRef = useRef(null);
    const instanceRef = useRef(null);
    const localBlobUrlRef = useRef(null); // Track blob URL created from IndexedDB for cleanup
    const { PSPDFKitLicenseKey } = getConfig()
    const [document, setDocument] = React.useState()
    const [loadingBackdrop, setLoadingBackdrop] = React.useState(false)
    const networkOnline = useNetworkOnline()
    const [showFilenameDialog, setShowFilenameDialog] = React.useState(false)
    const [pendingPdfBuffer, setPendingPdfBuffer] = React.useState(null)
    const [snackbarOpen, setSnackbarOpen] = React.useState({ open: false, message: '' })
    const [showImportDialog, setShowImportDialog] = React.useState(false)
    let instance;

    const dbKey = 'localdocuments-' + props.generalformsgoogledriveid + '-' + props.woid
    const addDBRecord = async (id, jsonString) => {
        return db.workreporting.put({ json: jsonString, uuid: id }, id)
    }

    const getDBRecord = async (id) => {
        const data = await db.workreporting.get(id);
        return data;
    }

    const writeDatabaseRecord = (key, dataToSave) => {
        if (dataToSave) {
            const newData = JSON.stringify(dataToSave)
            addDBRecord(key, newData).then(
                id => {
                    console.log('ID Generated: ', id);
                },
                error => {
                    console.log(error);
                }
            )
        }
    }

    const loadDatabaseRecord = async (key) => {
        const record = await getDBRecord(key)
        if (record) {
            return JSON.parse(record.json)
        }
        else {
            return undefined
        }
    }

    useEffect(() => {
        // getDBLocalDocumentsRecord()
        loadDatabaseRecord(dbKey).then(record => {
            if (record) {
                const docData = record
                if (docData.document) {
                    var contentType = 'application/pdf';

                    const byteCharacters = atob(docData.document)
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], {type: contentType});
                    const url = window.URL.createObjectURL(blob);
                    localBlobUrlRef.current = url; // Track for cleanup
                    setDocument(url)
                }
                else {
                    setDocument(props.document)
                }
            }
            else {
                setDocument(props.document)
            }
        })

    }, [])

    const generateDefaultFilename = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;

        const baseFilename = props.filename ? props.filename.replace('.pdf', '') : 'document';
        return `${baseFilename}_${timestamp}.pdf`;
    };

    const downloadPdfWithFilename = (buffer, filename) => {
        const blob = new Blob([buffer], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleExportPDF = () => {
        instanceRef.current.exportPDF().then((buffer) => {
            setPendingPdfBuffer(buffer);
            setShowFilenameDialog(true);
        }).catch((error) => {
            console.error("Export PDF error:", error);
            alert("Failed to export PDF");
        });
    };

    const handleFlattenPDF = async () => {
        try {
            // Save any pending form field changes before flattening
            await instanceRef.current.save();

            const buffer = await instanceRef.current.exportPDF({ flatten: true });
            setPendingPdfBuffer(buffer);
            setShowFilenameDialog(true);
        } catch (error) {
            console.error("Flatten PDF error:", error);
            alert("Failed to flatten PDF");
        }
    };

    const handleFilenameConfirm = (filename) => {
        if (pendingPdfBuffer) {
            downloadPdfWithFilename(pendingPdfBuffer, filename);
        }
        setShowFilenameDialog(false);
        setPendingPdfBuffer(null);
    };

    const handleFilenameCancel = () => {
        setShowFilenameDialog(false);
        setPendingPdfBuffer(null);
    };

    const isImageFile = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'].includes(ext);
    };

    const getImageContentType = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        const types = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'tiff': 'image/tiff'
        };
        return types[ext] || 'image/jpeg';
    };

    const handleImportFromDocuments = async (selectedDocs) => {
        // selectedDocs can be a single doc {id, name} or an array of docs
        const docs = Array.isArray(selectedDocs) ? selectedDocs : [selectedDocs];

        // Close dialog first so spinner is visible
        setShowImportDialog(false);

        try {
            setLoadingBackdrop(true);
            const pdfInstance = instanceRef.current;
            const PSPDFKit = await import("@nutrient-sdk/viewer");

            if (!pdfInstance) {
                throw new Error("PDF viewer not initialized");
            }

            let currentPageIndex = pdfInstance.viewState.currentPageIndex;

            for (const doc of docs) {
                const { url } = await fetchDocumentAsBlob({ id: doc.id });
                const response = await fetch(url);
                const blob = await response.blob();

                if (isImageFile(doc.name)) {
                    // For images: add a new blank page and insert image as annotation
                    const pageInfo = pdfInstance.pageInfoForIndex(0);
                    const pageWidth = pageInfo.width;
                    const pageHeight = pageInfo.height;

                    // Load image to get its natural dimensions
                    const imageUrl = URL.createObjectURL(blob);
                    const img = new Image();
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = imageUrl;
                    });
                    const imgWidth = img.naturalWidth;
                    const imgHeight = img.naturalHeight;
                    URL.revokeObjectURL(imageUrl);

                    // Add a new blank page after current page
                    await pdfInstance.applyOperations([{
                        type: "addPage",
                        afterPageIndex: currentPageIndex,
                        backgroundColor: new PSPDFKit.Color({ r: 255, g: 255, b: 255 }),
                        pageWidth: pageWidth,
                        pageHeight: pageHeight,
                        rotateBy: 0
                    }]);

                    const newPageIndex = currentPageIndex + 1;

                    // Create attachment from image blob
                    const imageAttachmentId = await pdfInstance.createAttachment(blob);

                    // Calculate dimensions that respect aspect ratio and fit within page
                    const margin = 20;
                    const maxWidth = pageWidth - (margin * 2);
                    const maxHeight = pageHeight - (margin * 2);
                    const imgAspectRatio = imgWidth / imgHeight;
                    const pageAspectRatio = maxWidth / maxHeight;

                    let finalWidth, finalHeight;
                    if (imgAspectRatio > pageAspectRatio) {
                        // Image is wider than page area - fit to width
                        finalWidth = maxWidth;
                        finalHeight = maxWidth / imgAspectRatio;
                    } else {
                        // Image is taller than page area - fit to height
                        finalHeight = maxHeight;
                        finalWidth = maxHeight * imgAspectRatio;
                    }

                    // Center the image on the page
                    const left = margin + (maxWidth - finalWidth) / 2;
                    const top = margin + (maxHeight - finalHeight) / 2;

                    const annotation = new PSPDFKit.Annotations.ImageAnnotation({
                        pageIndex: newPageIndex,
                        contentType: getImageContentType(doc.name),
                        imageAttachmentId,
                        description: doc.name,
                        boundingBox: new PSPDFKit.Geometry.Rect({
                            left: left,
                            top: top,
                            width: finalWidth,
                            height: finalHeight
                        })
                    });

                    await pdfInstance.create(annotation);
                    currentPageIndex = newPageIndex;
                } else {
                    // For PDFs and other documents: use importDocument
                    await pdfInstance.applyOperations([{
                        type: "importDocument",
                        afterPageIndex: currentPageIndex,
                        treatImportedDocumentAsOnePage: false,
                        document: blob
                    }]);

                    // Update page index for next document (get total pages after import)
                    currentPageIndex = pdfInstance.totalPageCount - 1;
                }
            }
        } catch (error) {
            console.error("Import error:", error);
            alert("Failed to import document: " + error.message);
        } finally {
            setLoadingBackdrop(false);
        }
    };

    const saveDocument = () => {
        instanceRef.current.exportPDF().then((buffer) => {
            const blob = new Blob([buffer], { type: "application/pdf" });
            var callbackProps = {instance: instance, ...props.item, woid: props.woid, blob: blob, setLoadingBackdrop: setLoadingBackdrop}
            if (props.generalformsgoogledriveid) {
                callbackProps['generalformsgoogledriveid'] = props.generalformsgoogledriveid
            }
            if (props.filename) {
                callbackProps['filename'] = props.filename
            }
            if (props.generalformsid) {
                callbackProps['generalformsid'] = props.generalformsid
            }

            props.saveDocumentCallback(callbackProps)

            // remove local copy
            db.workreporting.get(dbKey).then(
                (record) => {
                    db.workreporting.delete(dbKey)
                })
        });
    }



    const handleClose = (event, reason) => {
        // if (reason === 'clickaway') {
        //     return;
        // }

        setSnackbarOpen({ open: false, message: ""  });
    }

    useEffect(()=> {
        if (props.saveDocument > 0) {
            saveDocument()
        }
    }, [props.saveDocument])

    const saveButton = {
        type: "custom",
        id: "save-pdf",
        // disabled: networkOnline == false,
        // title: networkOnline ? "Save" : "Offline",
        title: "Save",
        onPress: () => {
            console.log(props)
            if (navigator.onLine) {
                saveDocument()
            }
            else {
                setSnackbarOpen({open: true, message: "You are currently offline and unable to save changes. When back in connectivity, click Save again."})
            }
        }
    };

    useEffect(() => {
        if (document && document?.length > 0) {
            const container = containerRef.current;
            let PSPDFKit, Options, I18n, InteractionMode, defaultAnnotationPresets;

            (async function() {
                debugger
                try {
                    const { NutrientViewer } = window;
                    NutrientViewer.unload(container);
                    PSPDFKit = NutrientViewer.default;
                    Options = NutrientViewer.Options;
                    I18n = NutrientViewer.I18n;
                    InteractionMode = NutrientViewer.InteractionMode;
                    defaultAnnotationPresets = NutrientViewer.defaultAnnotationPresets;
                }
                catch (e) {
                    console.log(e)
                }

                try {
                    Options.ENABLE_INK_SMOOTH_LINES = true;
                    Options.INK_EPSILON_RANGE_OPTIMIZATION = 0;
                    Options.TEXT_ANNOTATION_AUTOFIT_BOUNDING_BOX_ON_EDIT = false

                    await I18n.preloadLocalizationData("en");
                    if (I18n.messages && I18n.messages.en) {
                        I18n.messages.en.cancel = "Cancel Changes";
                        I18n.messages.en.saveAs = "Download";
                        I18n.messages.en.save = "Close";
                    }
                }
                catch (i18nError) {
                    console.warn("I18n customization not available:", i18nError);
                }

                const saveButton = {
                    type: "custom",
                    id: "save-pdf",
                    disabled: !props?.item?.allowsave,
                    title: "Save",
                    onPress: () => {
                        saveDocument()
                    }
                };

                const extractPagesButton = {
                    type: "custom",
                    id: "extract-pages",
                    title: "Extract Pages",
                    onPress: () => {
                        // Enter Document Editor mode which has built-in extract-pages functionality
                        // ✅ CHANGED: Use InteractionMode directly instead of PSPDFKit.InteractionMode
                        instance.setViewState(viewState =>
                            viewState.set("interactionMode", InteractionMode.DOCUMENT_EDITOR)
                        );
                    }
                };

                // Compact toolbar using dropdown groups to avoid horizontal scroll
                const items = [];

                // Add save button first (left side) if user has permission
                if (!props?.readonly) {
                    items.push(saveButton);
                }

                items.push(
                    // Sidebar navigation
                    { type: "sidebar-thumbnails" },
                    { type: "sidebar-annotations" },
                    { type: "sidebar-bookmarks" },
                    { type: "sidebar-layers" },

                    // Page navigation & viewing
                    { type: "pager" },
                    { type: "zoom-out" },
                    { type: "zoom-in" },
                    { type: "zoom-mode" },

                    { type: "spacer" },

                    // Edit history
                    { type: "undo" },
                    { type: "redo" },

                    // Annotation tools (flat list for proper selection state)
                    { type: "signature" },
                    { type: "text" },
                    { type: "ink" },
                    { type: "text-highlighter" },
                    { type: "note" },
                    { type: "ink-eraser" },

                    // Redaction tools
                    { type: "redact-text-highlighter" },
                    { type: "redact-rectangle" },

                    // Shape tools
                    { type: "rectangle" },
                    { type: "arrow" },
                    { type: "line" },
                    { type: "ellipse" },

                    // Document operations
                    { type: "image"},
                    { type: "search" },
                    { type: "print" },
                    { type: "document-editor" },
                    {
                        type: "custom",
                        id: "export-pdf-custom",
                        title: "Export PDF",
                        onPress: handleExportPDF
                    },
                    {
                        type: "custom",
                        id: "flatten-pdf",
                        title: "Flatten",
                        onPress: handleFlattenPDF
                    },
                    {
                        type: "custom",
                        id: "import-from-documents",
                        title: "Import from WO",
                        onPress: () => setShowImportDialog(true)
                    },
                );

                // Add the extract pages button to the toolbar (available to all users)
                if (!props?.readonly) {
                    items.push(extractPagesButton);
                }
                const instance = await PSPDFKit.load({
                    // Container where PSPDFKit should be mounted.
                    container,
                    // useCDN: true,
                    // live
                    licenseKey: PSPDFKitLicenseKey,
                    enableStylusMode: false,
                    inkEraserMode: "POINT",
                    // The document to open.
                    document: props.document,
                    toolbarItems: items,
                    enableServiceWorkerSupport: true,
                    // autoSaveMode: "IMMEDIATE",
                    // Enable history for undo/redo functionality
                    enableHistory: true,
                    enableClipboardActions: true,
                    enableContentEditing: true,
                    // Use the public directory URL as a base URL. PSPDFKit will download its library assets from here.
                    baseUrl: `${window.location.protocol}//${window.location.host}/static/`,
                    enableAutomaticLinkExtraction: false,
                    // Configure default pen size and other annotation defaults
                    // ✅ CHANGED: Use defaultAnnotationPresets directly instead of PSPDFKit.defaultAnnotationPresets
                    annotationPresets: {
                        ...defaultAnnotationPresets,
                        ink: {
                            ...defaultAnnotationPresets.ink,
                            lineWidth: 1  // Smallest possible pen size
                        },
                        highlighter: {
                            ...defaultAnnotationPresets.highlighter,
                            lineWidth: 1  // Smallest highlighter width
                        },
                        text: {
                            ...defaultAnnotationPresets.text,
                            fontSize: 12
                        }
                    },
                });

                // Store instance in ref for access outside useEffect
                instanceRef.current = instance;

                // instance.setViewState(v =>
                //     v.set("interactionMode", PSPDFKit.InteractionMode.CONTENT_EDITOR)
                // )

                if (props?.readonly) {
                    instance.setToolbarItems([]);
                }

                instance.addEventListener("document.change", handleDocumentChange);
                instance.addEventListener("document.saveStateChange", saveStateChange)
            })();

            const handleDocumentChange = () => {
                URL.revokeObjectURL(props.document);
            }

            const saveStateChange = () => {
                instanceRef.current.save().then(() => {
                    saveDocumentLocal()
                })
            }

            const saveDocumentLocal = async () => {
                instanceRef.current.exportPDF().then((buffer) => {
                    const blob = new Blob([buffer], { type: "application/pdf" });

                    var reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = function() {
                        var base64data = reader.result;
                        const dataSpit = base64data.split(';base64,')[1]

                        const recordToSave = {
                            filename: props.filename,
                            decryptedData: props.decryptedData,
                            date: new Date(),
                            document: dataSpit
                        }
                        writeDatabaseRecord(dbKey, recordToSave)
                    }
                })
            }

            return () => {
                if (instanceRef.current) {
                    instanceRef.current.removeEventListener("document.change", handleDocumentChange);
                }
                PSPDFKit && PSPDFKit.unload(container);
            };
        }
    }, [document]);

    return (
        <div>
            <>
                <Snackbar
                    open={snackbarOpen.open}
                    autoHideDuration={10000}
                    onClose={handleClose}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                >
                    <Alert severity="warning">
                        {snackbarOpen.message}
                    </Alert>
                </Snackbar>
            </>
            <LoadingBackdrop open={loadingBackdrop} />
            <div ref={containerRef} style={{ width: "100%", height: "100dvh"}}></div>
            <PdfFilenameDialog
                open={showFilenameDialog}
                defaultFilename={generateDefaultFilename()}
                onConfirm={handleFilenameConfirm}
                onCancel={handleFilenameCancel}
            />
            <ImportFromDocumentsDialog
                open={showImportDialog}
                woid={props.woid}
                onSelect={handleImportFromDocuments}
                onCancel={() => setShowImportDialog(false)}
            />
        </div>

    );
}
